/**
 * Data de conclusão de leitura — conversões ISO ⇄ `Date`.
 *
 * ## Por que este módulo existe (e por que não é só `new Date(iso)`)
 *
 * A data de conclusão é digitada como **dia do calendário** ("terminei em 1º de
 * janeiro"), mas é gravada como `Timestamp`, que é um **instante**. A conversão
 * entre os dois é onde mora o bug:
 *
 *   new Date("2026-01-01")      // → 2026-01-01T00:00:00Z (meia-noite UTC!)
 *
 * O construtor nativo lê ISO só-data como **UTC**. Em Brasília (UTC−3) esse
 * instante é `31/12/2025 21:00` — e **todos** os consumidores da data leem em
 * horário local:
 *
 *   · `Home.tsx`                → `finishedAt.toDate().getFullYear()` (meta anual)
 *   · `useProfileState`         → livros do ano / do mês
 *   · a estante                 → seções por ano
 *   · `digests/{YYYY-MM}`       → o mês em que o livro conta na `/insights`
 *
 * Resultado medido: um livro concluído em **1º de janeiro** contava para o ano
 * **anterior**, em todas essas superfícies de uma vez. O espelho do mesmo erro
 * está no default do formulário — `new Date().toISOString().slice(0,10)` vira
 * "amanhã" depois das 21h no Brasil, então a data pré-preenchida (e o teto de
 * "sem data futura") ficavam um dia à frente.
 *
 * A correção é ancorar o dia do calendário no **meio-dia local**: sobra 12h de
 * folga para qualquer fuso do planeta e para os saltos de horário de verão, de
 * modo que o dia lido de volta é sempre o dia digitado.
 *
 * ⚠️ **Local é o frame CERTO aqui, e não uma violação da regra de ouro nº 5**
 * (`dayKeyForBR`). Aquela regra governa lógica de negócio no SERVIDOR —
 * streak, cap diário, idempotência —, onde o dia precisa ser o mesmo para
 * todos. Aqui a pergunta é "em que dia VOCÊ terminou o livro", a resposta é o
 * calendário do próprio usuário, e o frame só precisa ser **o mesmo** da
 * leitura — que é local em todas as superfícies acima.
 */

/** `YYYY-MM-DD`. Formato do `<input type="date">` e do `DatePickerInput`. */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Converte `YYYY-MM-DD` no **meio-dia local** daquele dia.
 *
 * Devolve `null` para entrada malformada ou para data que não existe
 * (`2026-02-31`) — o `Date` nativo faria rollover silencioso para 3 de março,
 * e uma data inventada é pior que uma recusada.
 */
export function isoToLocalDate(iso: string): Date | null {
  const m = ISO_DATE_RE.exec(iso.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  // Rollover detection: `new Date(2026, 1, 31)` vira 3 de março sem reclamar.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/** `Date` → `YYYY-MM-DD` no calendário LOCAL (nunca via `toISOString`). */
export function dateToIsoLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Hoje no calendário local. Substitui `toISOString().slice(0,10)`, que é UTC. */
export function todayIsoLocal(now: Date = new Date()): string {
  return dateToIsoLocal(now);
}

/**
 * Ano de um instante, no calendário LOCAL — a primitiva única de "em que ano
 * isto aconteceu".
 *
 * Existe para que a estante (divisória de ano), a meta anual e o Wrapped
 * respondam com o MESMO ano para o mesmo livro. Quando cada superfície
 * derivava o ano por conta própria, um livro terminado na virada podia cair em
 * 2026 na estante e contar em 2025 na meta — e ninguém conseguia dizer qual
 * das duas estava certa.
 *
 * `dateToIsoLocal` e não `getFullYear()` direto: é a mesma função que grava a
 * data, então ler e escrever usam o mesmo caminho.
 */
export function yearOfLocal(ms: number): number {
  return Number(dateToIsoLocal(new Date(ms)).slice(0, 4));
}

/** Ano corrente no calendário local. */
export function currentYearLocal(now: Date = new Date()): number {
  return now.getFullYear();
}

/**
 * A data é futura em relação a `now`?
 *
 * Compara DIA com DIA, não instante com instante: terminar um livro "hoje de
 * manhã" às 22h não pode ser recusado como futuro.
 */
export function isFutureIsoDate(iso: string, now: Date = new Date()): boolean {
  return iso > todayIsoLocal(now);
}

/**
 * Qualquer coisa com `toDate()` (Timestamp do Firestore) → `YYYY-MM-DD` local.
 * `null` quando não há data — que é o caso do livro marcado como lido sem
 * declarar quando, e é exatamente o que a UI de edição existe para consertar.
 */
export function timestampToIsoLocal(
  ts: { toDate?: () => Date } | null | undefined,
): string | null {
  const date = ts?.toDate?.();
  if (!date || Number.isNaN(date.getTime())) return null;
  return dateToIsoLocal(date);
}

/**
 * Rótulo curto do estado atual, para o gatilho de edição na BookDetail.
 *
 * Mora aqui, e não junto do sheet, por uma razão de ferramenta: um arquivo de
 * componente que também exporta função quebra o Fast Refresh do Vite (regra
 * `react-refresh/only-export-components`), e o sheet é lazy.
 */
export function finishedDateLabel(
  userBook: { finishedAt?: { toDate?: () => Date } | null },
  now: Date = new Date(),
): string {
  const iso = timestampToIsoLocal(userBook.finishedAt);
  if (!iso) return "Sem data definida";
  const [y, m, d] = iso.split("-");
  const label = `${d}/${m}/${y}`;
  return iso === todayIsoLocal(now) ? `Hoje · ${label}` : label;
}

/**
 * Versão curta para o card da estante — `dd/mm/aaaa` ou "Sem data".
 *
 * Sem o prefixo "Hoje ·" do rótulo longo: no card de 3 colunas a 375px sobram
 * ~105px, e a linha quebraria. Numérico e não "12 de mar": em pt-BR a forma
 * numérica é inequívoca e ocupa metade do espaço.
 */
export function finishedDateShort(userBook: {
  finishedAt?: { toDate?: () => Date } | null;
}): string {
  return shortDateOrFallback(userBook.finishedAt, "Sem data");
}

/**
 * Rótulo do chip de INÍCIO no hero do livro em leitura — `dd/mm/aaaa`.
 *
 * Sem data o chip diz "Início" e não "Sem data": ele fica ao lado do chip
 * de status, sem legenda própria, e "Sem data" solto não diz data DE QUÊ. É
 * também o estado que pede ação (o chip vai em `accent`), então o rótulo é
 * o nome do que está faltando.
 */
export function startedDateShort(userBook: {
  startedAt?: { toDate?: () => Date } | null;
}): string {
  return shortDateOrFallback(userBook.startedAt, "Início");
}

/**
 * BUG-295 — tira o `/aaaa` do rótulo curto quando o ano é o corrente, para os
 * chips do hero: "Lendo agora · pág. 78 de 210" + "22/09/2026" somavam 366 px
 * e quebravam em duas linhas em qualquer celular abaixo de ~410 px de largura.
 * Ano passado continua explícito — é aí que o ano informa algo. Rótulo sem
 * data (o fallback) passa intacto.
 */
export function withoutCurrentYear(label: string, now: Date = new Date()): string {
  const suffix = `/${now.getFullYear()}`;
  return /^\d{2}\/\d{2}\/\d{4}$/.test(label) && label.endsWith(suffix)
    ? label.slice(0, -suffix.length)
    : label;
}

function shortDateOrFallback(
  ts: { toDate?: () => Date } | null | undefined,
  fallback: string,
): string {
  const iso = timestampToIsoLocal(ts);
  if (!iso) return fallback;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
